import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

const LS_COLLAPSED = 'navV2.collapsed';
const LS_OPEN = 'navV2.openSections';
const LS_PINNED = 'navV2.pinned';
const MAX_PINS = 8;

/**
 * Single source of truth for sidebar v2 state.
 * One router subscription app-wide; menu components are pure renderers.
 */
@Injectable({ providedIn: 'root' })
export class NavV2Service {
  private router = inject(Router);

  /** Pinned collapsed/expanded — user-controlled only, persisted. */
  collapsed = signal(localStorage.getItem(LS_COLLAPSED) === '1');
  /** Manually/auto opened accordion sections. Add-only on navigation. */
  openSections = signal<ReadonlySet<string>>(new Set(this.readOpen()));
  /** User-pinned leaf routes (keys = url, or item id for action items). */
  pinned = signal<readonly string[]>(this.readPinned());
  /** Current url (no query string), kept by the single router subscription. */
  activeUrl = signal(this.normalize(this.router.url));

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.activeUrl.set(this.normalize(e.urlAfterRedirects)));
  }

  toggleCollapsed(): void {
    this.collapsed.update((c) => !c);
    localStorage.setItem(LS_COLLAPSED, this.collapsed() ? '1' : '0');
  }

  toggleSection(id: string): void {
    this.openSections.update((s) => {
      const next = new Set(s);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    this.persistOpen();
  }

  /** Add-only — used to reveal the section containing the active route. */
  openSection(id: string): void {
    if (this.openSections().has(id)) return;
    this.openSections.update((s) => new Set(s).add(id));
    this.persistOpen();
  }

  isSectionOpen(id: string): boolean {
    return this.openSections().has(id);
  }

  /** Toggle a pin. Adding past MAX_PINS is a no-op. */
  togglePin(key: string): void {
    this.pinned.update((list) => {
      if (list.includes(key)) return list.filter((k) => k !== key);
      if (list.length >= MAX_PINS) return list;
      return [...list, key];
    });
    localStorage.setItem(LS_PINNED, JSON.stringify(this.pinned()));
  }

  isPinned(key: string): boolean {
    return this.pinned().includes(key);
  }

  /** Same matching rules as the legacy menu: exact, or prefix at a segment boundary. */
  isRouteActive(menuUrl: string | undefined, currentUrl: string): boolean {
    if (!menuUrl) return false;
    const normalized = menuUrl.startsWith('/') ? menuUrl : '/' + menuUrl;
    if (normalized === '/') return currentUrl === '/';
    return currentUrl === normalized || currentUrl.startsWith(normalized + '/');
  }

  private normalize(url: string): string {
    return url.split('?')[0] || '/';
  }

  private readOpen(): string[] {
    try {
      return JSON.parse(localStorage.getItem(LS_OPEN) ?? '[]');
    } catch {
      return [];
    }
  }

  private readPinned(): string[] {
    try {
      return JSON.parse(localStorage.getItem(LS_PINNED) ?? '[]');
    } catch {
      return [];
    }
  }

  private persistOpen(): void {
    localStorage.setItem(LS_OPEN, JSON.stringify([...this.openSections()]));
  }
}
